import { TextField, Box, Button, CircularProgress, Paper } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../store/hooks'
import { createStartup, fetchStartupDetails, getUploadLink, updateStartup, uploadFile } from '../../../../networking/network'
import { useForm, Controller } from 'react-hook-form'
import Startup, { StartupUpload } from '../../../../models/startup'
import { useNavigate, useParams } from 'react-router-dom'
import FileUploadElement, { FileUploadIcon } from '../../reusable/FileUploadElement'

interface Props {

}

const extractLastComponent = (urlPath: string) => {
  const url = new URL(urlPath).pathname.split('/')
  const last = url.pop() || url.pop() // Handle potential trailing slash
  return last
}

const UploadStartup = (props: Props) => {
  const { control, handleSubmit, reset } = useForm()

  const { id } = useParams()

  const navigate = useNavigate()

  const [isUploading, setIsUploading] = useState(false)

  const [isLoadingStartup, setIsLoadingStartup] = useState(false)

  const [startup, setStartup] = useState<Startup | undefined>(undefined)

  let uploadedDeckURL: string | undefined
  let uploadedLogoURL: string | undefined

  const [deck, setDeck] = useState<any>(null)
  const [logo, setLogo] = useState<any>(null)

  const accessToken = useAppSelector(state => state.user.token)

  useEffect(() => {
    reset(startup)
  }, [startup])

  useEffect(() => {
    if (!id || !accessToken) {
      return
    }
    const loadStartup = async () => {
      setIsLoadingStartup(true)
      const fetchedStartup = await fetchStartupDetails(accessToken, id!)
      setStartup(fetchedStartup)
      setIsLoadingStartup(false)
    }

    loadStartup()
  }, [id])

  const uploadFiles = async () => {
    if (!accessToken) {
      return
    }
    const uploadDeckPromise = async () => {
      if (deck) {
        const uploadLink = await getUploadLink(accessToken, deck)
        if (uploadLink !== null && deck !== null) {
          await uploadFile(uploadLink, deck)
          const uploadURL = extractLastComponent(uploadLink)
          uploadedDeckURL = uploadURL
        }
      }
    }

    const uploadLogoPromise = async () => {
      if (!accessToken) {
        return
      }

      if (logo) {
        const uploadLink = await getUploadLink(accessToken, logo)
        if (uploadLink !== null && logo !== null) {
          await uploadFile(uploadLink, logo)
          const uploadURL = extractLastComponent(uploadLink)
          uploadedLogoURL = uploadURL
        }
      }
    }

    return await Promise.all([
      uploadDeckPromise(),
      uploadLogoPromise()
    ])
  }

  const handleFormSubmit = (data: any) => {
    if (!accessToken) {
      return
    }
    const submitForm = async () => {
      setIsUploading(true)
      await uploadFiles()
      let upload: StartupUpload

      if (!startup) {
        upload = {
          created: new Date(),
          description: data.description,
          name: data.name
        }
      } else {
        upload = {
          id: startup.id,
          created: startup.createdDate,
          description: data.description || startup.description,
          name: data.name || startup.name
        }
      }

      if (data.contactName) {
        upload.contact_name = data.contactName
      } else {
        upload.contact_name = startup?.contactName
      }

      if (data.contactEmail) {
        upload.contact_email = data.contactEmail
      } else {
        upload.contact_email = startup?.contactEmail
      }

      if (data.companyEmail) {
        upload.company_email = data.companyEmail
      } else {
        upload.company_email = startup?.companyEmail
      }

      if (data.companyPhone) {
        upload.company_phone = data.companyPhone
      } else {
        upload.company_phone = startup?.companyPhone
      }

      if (data.companyWebsite) {
        upload.company_website = data.companyWebsite
      } else {
        upload.company_website = startup?.companyWebsite
      }

      if (uploadedLogoURL !== undefined) {
        upload.logo_url = encodeURIComponent(uploadedLogoURL)
      } else if (startup) {
        upload.logo_url = startup.logoUrl
      }

      if (uploadedDeckURL !== undefined) {
        upload.deck_url = encodeURIComponent(uploadedDeckURL)
      } else if (startup) {
        upload.deck_url = startup.deckUrl
      }

      if (data.videoUrl) {
        upload.video_url = data.videoUrl
      } else {
        upload.video_url = startup?.videoUrl
      }

      if (data.naics) {
        upload.naics_code = data.naics
      } else {
        upload.naics_code = startup?.naics
      }

      if (!startup) {
        const result = await createStartup(accessToken, upload)
        navigate(`/startups/${result.id}`)
      } else if (id) {
        const result = await updateStartup(accessToken, id, upload)
        setStartup(result)
      }
      setIsUploading(false)
    }

    submitForm()
  }

  return (
    <Paper sx={{
      margin: '16px'
    }}>
    {(isUploading || isLoadingStartup) &&
      <Box display='flex' justifyContent='space-around' sx={{
        width: '100%',
        padding: '16px'
      }}>
        <CircularProgress />
      </Box>
    }
    {!isUploading && !isLoadingStartup &&
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Box sx={{
          margin: '0px auto'
        }}>
          <Box display='flex' flexDirection='row' alignItems='top' justifyContent='center'>
            <Box display='flex' flexDirection='column' sx={{
              width: '500px',
              m: 2
            }}>
              <Controller 
                name='name'
                rules={{
                  required: 'Company name required'
                }}
                control={control}
                defaultValue={startup?.name || ''}
                render={({ field: { onChange, value }, fieldState: { error } }) => 
                  <TextField 
                    value={value} 
                    onChange={onChange} 
                    variant='outlined' 
                    error={!!error}
                    helperText={error ? error.message : null}
                    label='Company Name*' sx={{
                      m: 1
                    }} /> 
                }
              />
              <Controller 
                name='contactName'
                control={control}
                defaultValue={startup?.contactName || ''}
                rules={{
                  required: 'Contact person required'
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => 
                  <TextField 
                    value={value} 
                    onChange={onChange} 
                    variant='outlined' 
                    error={!!error}
                    helperText={error ? error.message : null}
                    label='Contact Person Name*' sx={{
                      m: 1
                    }} /> 
                }
              />
              <Controller 
                name='contactEmail'
                control={control}
                rules={{
                  required: 'Contact email required'
                }}
                defaultValue={startup?.contactEmail || ''}
                render={({ field: { onChange, value }, fieldState: { error } }) => 
                  <TextField 
                    value={value} 
                    onChange={onChange} 
                    variant='outlined' 
                    error={!!error}
                    helperText={error ? error.message : null}
                    label='Contact Email*' sx={{
                      m: 1
                    }} /> 
                }
              />
              <Controller 
                name='companyWebsite'
                control={control}
                defaultValue={startup?.companyWebsite || ''}
                render={({ field: { onChange, value }, fieldState: { error } }) =>  
                  <TextField 
                    onChange={onChange} 
                    value={value} 
                    variant='outlined' 
                    label='Website' sx={{
                      m: 1
                    }} 
                    error={!!error}
                  />
                }
              />
              <Controller 
                name='companyEmail'
                control={control}
                defaultValue={startup?.companyEmail || ''}
                render={({ field: { onChange, value }, fieldState: { error } }) => 
                  <TextField 
                    onChange={onChange} 
                    value={value} 
                    variant='outlined' 
                    label='Company Email' 
                    sx={{
                      m: 1
                    }}
                    error={!!error}
                  />
                }
              />
              <Controller 
                name='companyPhone'
                control={control}
                defaultValue={startup?.companyPhone || ''}
                render={({ field: { onChange, value }, fieldState: { error } }) => 
                  <TextField 
                    onChange={onChange} 
                    value={value} 
                    variant='outlined' 
                    label='Company Phone' sx={{
                      m: 1
                    }} />
                }
              />
            </Box>
            <Box display='flex' justifyContent='space-between' flexDirection='column' sx={{
              width: '500px',
              m: 2
            }}>
              <Box display='flex' flexDirection='row' sx={{
                m: 2,
                width: '100%',
                padding: 0
              }}>
                <FileUploadElement
                  fileSelected={(file) => {
                    setDeck(file)
                  }}
                  icon={FileUploadIcon.DECK}
                  diameter={73}
                  title='Upload Pitch Deck'
                  subtitle='File must be .pdf'
                  accepts='.pdf'
                  style={{
                    width: '45%',
                    marginRight: '16px'
                  }}
                />
                <FileUploadElement
                  fileSelected={(file) => {
                    setLogo(file)
                  }}
                  icon={FileUploadIcon.PLUS}
                  diameter={73}
                  title='Upload Company Logo'
                  subtitle='File must be .png or .jpg'
                  accepts='.jpg, .jpeg, .png'
                  style={{
                    width: '45%'
                  }}
                />
              </Box>
              <Controller 
                name='description'
                control={control}
                defaultValue={startup?.description || ''}
                render={({ field: { onChange, value }, fieldState: { error } }) => 
                  <TextField 
                    multiline 
                    rows={5} 
                    value={value}
                    variant='outlined' 
                    label='Brief Description' 
                    sx={{
                      m: 1
                    }} 
                    onChange={onChange}
                  />
                }
              />
              <Controller 
                name='naics'
                control={control}
                rules={{
                  pattern: /[0-9]/
                }}
                defaultValue={startup?.naics || ''}
                render={({ field: { onChange, value }, fieldState: { error } }) => 
                  <TextField type='number' value={value} variant='outlined' onChange={onChange} label='Naics Code' sx={{
                    m: 1
                  }} />
                }
              />
              <Controller 
                name='videoUrl'
                control={control}
                defaultValue={startup?.videoUrl || ''}
                render={({ field: { onChange, value }, fieldState: { error } }) => 
                  <TextField variant='outlined' value={value} onChange={onChange} label='Youtube Video Link' sx={{
                    m: 1
                  }} />
                }
              />
            </Box>
          </Box>
          <Box display='flex' flexDirection='row' alignItems='center' justifyContent='space-around' sx={{
            width: '100%',
            paddingBottom: '16px'
          }}>
            <Button type='submit' variant='contained' sx={{
              m: 1,
              width: '300px'
            }}>Submit</Button>
          </Box>
        </Box>
      </form>
    }
    </Paper>
  )
}

export default UploadStartup
